import React from 'react';
import { MapPin, Navigation, Eye, Award } from 'lucide-react';

export default function RegionalClassificationCard({ portugueseRegion }) {
    if (!portugueseRegion || portugueseRegion.region === 'outside_portugal') {
        return null;
    }

    const { regionName, district, nearestUrbanCenter, visualMarkers, confidence } = portugueseRegion;

    // Region-specific colors
    const regionColors = {
        'lisboa': 'from-emerald-50 to-emerald-100 border-emerald-300',
        'norte': 'from-blue-50 to-blue-100 border-blue-300',
        'algarve': 'from-orange-50 to-orange-100 border-orange-300',
        'centro': 'from-green-50 to-green-100 border-green-300',
        'alentejo': 'from-yellow-50 to-yellow-100 border-yellow-300',
        'madeira': 'from-pink-50 to-pink-100 border-pink-300',
        'acores': 'from-cyan-50 to-cyan-100 border-cyan-300'
    };

    const gradientClass = regionColors[portugueseRegion.region] || 'from-gray-50 to-gray-100 border-gray-300';

    return (
        <div className={`bg-gradient-to-br ${gradientClass} rounded-xl border-2 p-6 shadow-sm`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                    Portuguese Regional Analysis
                </h3>
                <div className="flex items-center gap-1 text-xs text-gray-600">
                    <Award className="w-3 h-3" />
                    <span>{(confidence * 100).toFixed(0)}% confident</span>
                </div>
            </div>

            {/* Region & District Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-600 font-medium mb-1">Region</p>
                    <p className="text-sm font-bold text-gray-900">{regionName}</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-gray-600 font-medium mb-1">District</p>
                    <p className="text-sm font-bold text-gray-900">{district}</p>
                </div>
            </div>

            {/* Nearest Urban Center */}
            {nearestUrbanCenter && (
                <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Navigation className="w-4 h-4 text-emerald-600" />
                                <p className="text-xs font-medium text-gray-600">Nearest City</p>
                            </div>
                            <p className="text-sm font-bold text-gray-900">
                                {nearestUrbanCenter.name}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-bold text-emerald-600">
                                {nearestUrbanCenter.distance.toFixed(1)}
                            </p>
                            <p className="text-xs text-gray-500">km away</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Visual Markers */}
            {visualMarkers && visualMarkers.length > 0 && (
                <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-4 h-4 text-emerald-600" />
                        <p className="text-xs font-semibold text-gray-700">Expected Visual Markers</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {visualMarkers.map(marker => {
                            // Special styling for Portuguese cultural markers
                            const isSpecial = ['azulejo_facades', 'calcada_portuguesa', 'pombalino_architecture'].includes(marker);

                            return (
                                <span
                                    key={marker}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all hover:scale-105 ${isSpecial
                                            ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md'
                                            : 'bg-emerald-100 text-emerald-800'
                                        }`}
                                >
                                    {marker.replace(/_/g, ' ')}
                                </span>
                            );
                        })}
                    </div>

                    {/* Cultural Heritage Note */}
                    {visualMarkers.some(m => ['azulejo_facades', 'calcada_portuguesa'].includes(m)) && (
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                            <p className="text-xs text-emerald-700 flex items-center gap-1">
                                <span className="font-semibold">🇵🇹 Portuguese Heritage:</span>
                                <span>This area features iconic Portuguese architectural elements</span>
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Footer */}
            <div className="mt-4 pt-4 border-t border-gray-300">
                <p className="text-xs text-gray-600">
                    <span className="font-semibold">ℹ️ Note:</span> Regional classification is based on GeoCLIP Phase 1
                    with {portugueseRegion.region === 'lisboa' || portugueseRegion.region === 'norte' ? '7 regions' : ''} Portuguese territory coverage.
                </p>
            </div>
        </div>
    );
}
